import {
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Tooltip,
} from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import { orderBy } from 'lodash';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { merchantsApi, shopGroupsApi, shopsApi } from 'api';
import {
  DataWrapper,
  FormControls,
  FormikSelect,
  FormikTextField,
  ListFilter,
  MerchantSelect,
  PageHeader,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey } from 'enums';
import { useCurrencies, useMutation, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Shop } from 'types';

type Values = {
  name: string;
  merchantId: string;
  assetCurrencyId: string;
};

export const ShopGroupDetailsPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role, isAdmin, isTechOperator } = useUser();

  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.shop_group_details',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const [selectedShopIds, setSelectedShopIds] = useState<Set<string>>(
    new Set(),
  );

  const isNew = useMemo(() => id === NEW_ID, [id]);
  const title = useMemo(
    () => (isNew ? t('title_create') : t('title_edit')),
    [isNew, t],
  );

  const backUrl = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.SHOP_GROUPS;
    }
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.SHOP_GROUPS;
    }
    return undefined;
  }, [isAdmin, isTechOperator]);

  const { assetCurrenciesOptions } = useCurrencies();

  const queryResultMerchants = useQuery(
    QueryKey.Merchants,
    () => merchantsApi.getAllAsRole(role)(),
    { enabled: isNew },
  );

  const requiredMsg = tCommon('errors.required');
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(requiredMsg),
        ...(isNew
          ? {
              merchantId: Yup.string().required(requiredMsg),
              assetCurrencyId: Yup.string().required(requiredMsg),
            }
          : {}),
      }),
    [isNew, requiredMsg],
  );

  const queryResultGroup = useQuery(
    [QueryKey.ShopGroups, id],
    () => shopGroupsApi.getOneAsRole(role)(id),
    {
      enabled: !isNew,
    },
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
    { enabled: !isNew },
  );

  const initialValues = useMemo<Values>(
    () => ({
      name: queryResultGroup.data?.name || '',
      merchantId: queryResultGroup.data?.merchantId || '',
      assetCurrencyId: queryResultGroup.data?.assetCurrencyId || '',
    }),
    [queryResultGroup.data],
  );

  const merchantShops = useMemo(() => {
    const shopGroup = queryResultGroup.data;
    if (!shopGroup) {
      return [] as Shop[];
    }

    return (queryResultShops.data || []).filter(
      (shop) => shop.merchantId === shopGroup.merchantId && !shop.archived,
    );
  }, [queryResultGroup.data, queryResultShops.data]);

  const serverGroupShopIds = useMemo(
    () =>
      new Set(
        merchantShops
          .filter((shop) => shop.shopGroupId === id)
          .map((shop) => shop.id),
      ),
    [merchantShops, id],
  );

  // Sync local state with server data
  useEffect(() => {
    setSelectedShopIds(new Set(serverGroupShopIds));
  }, [serverGroupShopIds]);

  const getShopDisabledReason = useCallback(
    (shop: Shop): string | null => {
      const shopGroup = queryResultGroup.data;
      if (!shopGroup) return null;
      if (shop.assetCurrencyId !== shopGroup.assetCurrencyId) {
        return t('shops.different_currency');
      }
      if (shop.shopGroupId && shop.shopGroupId !== shopGroup.id) {
        return t('shops.in_another_group');
      }
      return null;
    },
    [queryResultGroup.data, t],
  );

  const handleToggleShop = useCallback((shopId: string, checked: boolean) => {
    setSelectedShopIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(shopId);
      } else {
        next.delete(shopId);
      }
      return next;
    });
  }, []);

  const invalidateQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries(QueryKey.Shops),
      queryClient.invalidateQueries(QueryKey.ShopGroups),
    ]);
  }, [queryClient]);

  const { mutateAsync: createGroup } = useMutation(
    shopGroupsApi.createAsRole(role),
    {
      onSuccess: () => {
        invalidateQueries();
        navigate(backUrl!);
      },
    },
  );

  const { mutateAsync: updateGroup } = useMutation(
    shopGroupsApi.updateAsRole(role),
    {
      onSuccess: () => {
        invalidateQueries();
        navigate(backUrl!);
      },
    },
  );

  const handleSubmit = useCallback(
    async (values: Values, helpers: FormikHelpers<Values>) => {
      try {
        if (isNew) {
          await createGroup({
            name: values.name,
            merchantId: values.merchantId,
            assetCurrencyId: values.assetCurrencyId,
          } as any);
        } else {
          await updateGroup({
            id,
            data: {
              name: values.name,
              shops: Array.from(selectedShopIds).map((shopId) => ({
                id: shopId,
              })) as any,
            },
          });
        }
      } finally {
        helpers.setSubmitting(false);
      }
    },
    [createGroup, id, isNew, updateGroup, selectedShopIds],
  );

  const queryResults = useMemo(
    () => (isNew ? undefined : [queryResultGroup, queryResultShops]),
    [isNew, queryResultGroup, queryResultShops],
  );

  return (
    <Fragment>
      <PageHeader title={title}></PageHeader>
      <DataWrapper queryResult={queryResults} ignoreState={{ empty: isNew }}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form>
              <FormControls>
                <FormikTextField
                  label={t('fields.name')}
                  name="name"
                  type="text"
                  fullWidth
                  required
                  sx={{ maxWidth: 500 }}
                />
                {isNew && (
                  <Fragment>
                    <MerchantSelect
                      label={t('fields.merchant')}
                      name="merchantId"
                      merchants={queryResultMerchants.data}
                      required
                      fullWidth
                      sx={{ maxWidth: 500 }}
                    />
                    <FormikSelect
                      label={t('fields.asset_currency')}
                      name="assetCurrencyId"
                      options={assetCurrenciesOptions}
                      required
                      fullWidth
                      sx={{ maxWidth: 500 }}
                    />
                  </Fragment>
                )}
              </FormControls>

              {!isNew && (
                <div className="tw-mt-10" style={{ maxWidth: 500 }}>
                  <div className="tw-text-xl tw-mb-4">
                    {t('sections.current_shops', {
                      count: selectedShopIds.size,
                    })}
                  </div>
                  <ListFilter
                    items={merchantShops}
                    labelGetter={(shop: Shop) => shop.name}
                  >
                    {(filteredShops, renderInput) => (
                      <div>
                        {renderInput()}
                        <Paper elevation={0} sx={{ padding: 3 }}>
                          {!filteredShops.length ? (
                            <div>{t('shops.empty')}</div>
                          ) : (
                            <div className="tw-max-h-60 tw-overflow-auto tw-flex tw-flex-col">
                              {orderBy(filteredShops, [
                                (shop) => !!getShopDisabledReason(shop),
                                (shop) => shop.name,
                              ]).map((shop) => {
                                const disabledReason =
                                  getShopDisabledReason(shop);
                                const isSelected = selectedShopIds.has(shop.id);

                                const checkbox = (
                                  <FormControlLabel
                                    key={shop.id}
                                    control={
                                      <Checkbox
                                        checked={isSelected}
                                        disabled={!!disabledReason}
                                        onChange={(_, checked) =>
                                          handleToggleShop(shop.id, checked)
                                        }
                                        sx={{
                                          paddingTop: 1,
                                          paddingBottom: 1,
                                        }}
                                      />
                                    }
                                    label={shop.name}
                                  />
                                );

                                return disabledReason ? (
                                  <Tooltip
                                    key={shop.id}
                                    title={disabledReason}
                                    placement="right"
                                  >
                                    <div>{checkbox}</div>
                                  </Tooltip>
                                ) : (
                                  checkbox
                                );
                              })}
                            </div>
                          )}
                        </Paper>
                      </div>
                    )}
                  </ListFilter>
                </div>
              )}

              <div className="tw-mt-8">
                <Button
                  type="submit"
                  variant="outlined"
                  onClick={formik.submitForm}
                  disabled={formik.isSubmitting}
                >
                  {isNew ? tCommon('buttons.create') : tCommon('buttons.save')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DataWrapper>
    </Fragment>
  );
};
